import { Lock } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { Avatar } from '@/components/tree/Avatar'
import { photoSrcSet } from '@/lib/albums'
import { feedRatio } from '@/lib/homeBlend'
import { isPrivate, momentPhotoUrl, paragraphs, written, type MomentSummary } from '@/lib/moments'
import { firstName } from '@/lib/tree'
import { shapeOf } from '../../../shared/momentStyle'
import { ToldLabel } from './Told'

// Moments one after another, as they were posted, like Home's feed: who wrote it and the day they posted it above, its photo beneath as wide as a phone's screen (a
// column on bigger screens), then its title and the start of the story. A moment's photos swipe sideways; words and
// stories without a photo sit on a card of their own.
export function MomentFeed({ moments }: { moments: MomentSummary[] }) {
  return (
    <ul aria-label="Moments" className="-mx-5 flex flex-col gap-9 sm:mx-auto sm:w-full sm:max-w-xl sm:gap-12">
      {moments.map((moment, i) => (
        <li key={moment.id}>
          <FeedPost moment={moment} first={i === 0} />
        </li>
      ))}
    </ul>
  )
}

const sizes = '(width >= 40rem) 36rem, 100vw'

function FeedPost({ moment, first }: { moment: MomentSummary; first: boolean }) {
  const [at, setAt] = useState(0)
  const shape = shapeOf(moment)
  const lead = shape === 'words' ? undefined : moment.photos[0]
  const words = shape === 'words' ? moment.body.trim() || moment.title : paragraphs(moment.body)[0]
  return (
    <article>
      <Link
        to={`/moment/${moment.id}`}
        className="flex flex-col gap-3 rounded-[1.25rem] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <span className="flex items-center gap-2.5 px-5 sm:px-1">
          <Avatar person={moment.author} className="size-8 text-xs shadow-none" />
          <span className="min-w-0 flex-1 truncate text-sub font-semibold">
            {moment.mine ? 'You' : firstName(moment.author.name)}
          </span>
          {isPrivate(moment) && <Lock className="size-3.5 shrink-0 text-muted-foreground" aria-label="Only you" />}
          <span className="shrink-0 text-sub text-muted-foreground">
            <span className="sr-only">Posted </span>
            {written(moment.sharedAt ?? moment.createdAt)}
          </span>
        </span>
        {lead ? (
          <>
            <span className="relative block">
              <span
                className="flex snap-x snap-mandatory [scrollbar-width:none] overflow-x-auto overscroll-x-contain bg-secondary sm:rounded-[1.25rem] [&::-webkit-scrollbar]:hidden"
                onScroll={(e) => setAt(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth))}
              >
                {moment.photos.map((photo, i) => (
                  <img
                    key={photo.id}
                    src={momentPhotoUrl(photo.id, 'medium')}
                    srcSet={photoSrcSet({ ...photo, ref: photo.id }, ['medium', 'display'], momentPhotoUrl)}
                    sizes={sizes}
                    alt=""
                    loading={first && i === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    style={{ aspectRatio: feedRatio(lead.width, lead.height) }}
                    className="w-full shrink-0 snap-center object-cover"
                  />
                ))}
              </span>
              {moment.photoCount > 1 && (
                <span
                  aria-hidden="true"
                  className="absolute top-3 right-3 rounded-full bg-viewer/60 px-2.5 py-1 text-xs font-medium text-viewer-foreground tabular-nums"
                >
                  {Math.min(at + 1, moment.photos.length)}/{moment.photoCount}
                </span>
              )}
            </span>
            <span className="flex flex-col gap-1 px-5 sm:px-1">
              <span className="text-lg leading-snug font-semibold text-balance">{moment.title}</span>
              {words && <span className="line-clamp-3 text-pretty text-muted-foreground">{words}</span>}
              <ToldLabel moment={moment} />
            </span>
          </>
        ) : (
          <span className="mx-5 flex flex-col overflow-hidden rounded-[1.25rem] bg-card shadow-paper sm:mx-0">
            <span aria-hidden="true" className="words-bar h-1.5" />
            <span className="flex flex-col gap-2 p-5">
              {shape === 'words' ? (
                <>
                  <span className="sr-only">{moment.title}: </span>
                  <span className="line-clamp-8 text-xl leading-snug font-medium text-pretty">{words}</span>
                </>
              ) : (
                <>
                  <span className="text-xl leading-snug font-semibold text-balance">{moment.title}</span>
                  {words && <span className="line-clamp-8 text-pretty text-muted-foreground">{words}</span>}
                </>
              )}
              <ToldLabel moment={moment} />
            </span>
          </span>
        )}
      </Link>
    </article>
  )
}
